/**
 * # RangeInput
 *
 * A customizable range input (slider) component.
 *
 * ## Props
 *
 * - `className?: string`: Additional CSS class names to be applied to the range input element.
 * - `label: string`: The label text for the range input. Creates a label element and visually hidden text for accessibility.
 * - `onBlur?: (e?: React.FocusEvent<HTMLElement>) => void`: Event handler for the `blur` event.
 * - `onFocus?: () => {}`: Event handler for the `focus` event.
 * - `onMouseEnter?: () => {}`: Event handler for the `mouseenter` event.
 * - `onMouseLeave?: () => {}`: Event handler for the `mouseleave` event.
 * - `status?: 'negative' | 'positive'`: Indicates whether the range input is in a success state or an error state.
 * - `min?: number`: The minimum value of the range input. Defaults to `0`.
 * - `max?: number`: The maximum value of the range input. Defaults to `100`.
 * - `step?: number | string`: The step value for the range input. Defaults to `1`.
 * - `value?: number`: The current value of the range input.
 * - `defaultValue?: number`: The default value of the range input.
 * - `disabled?: boolean`: Indicates whether the range input is disabled.
 * - `error?: boolean`: Indicates whether the range input has an error. Use 'status' most of the time instead.
 * - `success?: boolean`: Indicates whether the range input is in a success state. Use 'status' most of the time instead.
 * - All standard HTML input attributes are also supported.
 *
 * ## Usage
 *
 * ```tsx
 * import React from 'react';
 * import RangeInput from './RangeInput';
 *
 * const Example = () => {
 *   const [value, setValue] = React.useState(50);
 *
 *   return (
 *     <RangeInput
 *       label="Volume"
 *       min={0}
 *       max={100}
 *       step={1}
 *       value={value}
 *       onChange={(e) => setValue(Number(e.target.value))}
 *     />
 *   );
 * };
 * ```
 */

import React, {forwardRef, InputHTMLAttributes, useState, useEffect, useRef} from 'react';
import classnames from 'classnames';
import makeDataProps from 'lib/makeDataProps';
import WithHoverAndFocus from '@albert-io/atomic/helpers/WithHoverAndFocus/WithHoverAndFocus.react';
import Label from '@albert-io/atomic/atoms/Label/Label.react';
import './range-input.scss';

// Generate unique IDs for range inputs
let idCounter = 0;
const generateId = () => `range-input-${++idCounter}`;

export interface Props
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>,
    PropsWithClassNameOptional {
  label: string;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  onFocus?: () => {};
  onMouseEnter?: () => {};
  onMouseLeave?: () => {};
  status?: 'negative' | 'positive';
  min?: number;
  max?: number;
  step?: number | string;
  value?: number;
  defaultValue?: number;
  error?: boolean;
  success?: boolean;
}

const determineStatusFromErrorSuccess = ({
  error,
  success
}: {
  error?: boolean;
  success?: boolean;
}) => {
  if (error) {
    return 'negative';
  }
  if (success) {
    return 'positive';
  }
  return undefined;
};

export const RangeInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      label,
      id,
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      min = 0,
      max = 100,
      step = 1,
      disabled,
      value,
      defaultValue,
      onChange,
      ...rest
    }: Props,
    ref
  ) => {
    const idRef = useRef<string | undefined>(undefined);
    if (!idRef.current && !id) {
      idRef.current = generateId();
    }
    const inputId = id || idRef.current;
    
    // Track current value for accessibility (visually hidden text)
    const [currentValue, setCurrentValue] = useState<number>(() => {
      if (value !== undefined) return value;
      if (defaultValue !== undefined) return defaultValue;
      return (min + max) / 2;
    });

    // Update currentValue when value prop changes (controlled component)
    useEffect(() => {
      if (value !== undefined) {
        setCurrentValue(value);
      }
    }, [value]);

    // Handle onChange to update currentValue for accessibility
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = Number(e.target.value);
      setCurrentValue(newValue);
      onChange?.(e);
    };

    let {status} = rest;
    const dataProps = makeDataProps({...rest});
    const passthroughProps = {...rest, ...dataProps};

    const {error, success} = passthroughProps;

    if (error || success) {
      status = determineStatusFromErrorSuccess({error, success});
    }

    return (
      <Label htmlFor={inputId} className="a-range-input__label">
        <WithHoverAndFocus
          customOnBlur={onBlur}
          customOnFocus={onFocus}
          customOnMouseEnter={onMouseEnter}
          customOnMouseLeave={onMouseLeave}
        >
          {({hasFocus, hasHover, ...handlers}) => {
            const classNames = classnames('a-range-input', className, {
              'a-range-input--error': status === 'negative',
              'a-range-input--focus': hasFocus,
              'a-range-input--hover': hasHover,
              'a-range-input--success': status === 'positive',
              'a-range-input--disabled': disabled
            });
            return (
              <>
                <input
                  ref={ref}
                  type="range"
                  id={inputId}
                  className={classNames}
                  min={min}
                  max={max}
                  step={step}
                  disabled={disabled}
                  {...(value !== undefined ? {value} : {defaultValue})}
                  {...passthroughProps}
                  {...handlers}
                  onChange={handleChange}
                />
                <span className="sr-only">
                  {label}: {currentValue}
                </span>
              </>
            );
          }}
        </WithHoverAndFocus>
      </Label>
    );
  }
);

RangeInput.displayName = 'RangeInput';

export default RangeInput;

